// Generic IndexedDB-backed object store (keyPath "id", newest-first listing)
// plus the small JSON-file helpers shared by the model collection and the
// floor-plan library. Each library gets its own database so the two can
// version/migrate independently.

export interface IDBStore<T extends { id: string; updatedAt: number }> {
  list(): Promise<T[]>; // newest first
  listKeys(): Promise<string[]>; // ids only — cheap (no record deserialize)
  get(id: string): Promise<T | undefined>;
  put(item: T): Promise<IDBValidKey>;
  remove(id: string): Promise<undefined>;
}

export function createIDBStore<T extends { id: string; updatedAt: number }>(
  dbName: string,
  storeName: string,
): IDBStore<T> {
  let _dbPromise: Promise<IDBDatabase> | null = null;
  function db(): Promise<IDBDatabase> {
    if (_dbPromise) return _dbPromise;
    _dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(dbName, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(storeName)) {
          req.result.createObjectStore(storeName, { keyPath: "id" });
        }
      };
      req.onsuccess = () => {
        // Close (and drop the cached handle) when another context asks to
        // delete/upgrade this database — without this, a cached connection
        // blocks account-deletion's local wipe forever.
        req.result.onversionchange = () => {
          req.result.close();
          _dbPromise = null;
        };
        resolve(req.result);
      };
      req.onerror = () => reject(req.error);
    });
    return _dbPromise;
  }

  function tx<R>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<R>,
  ): Promise<R> {
    return db().then(
      (d) =>
        new Promise<R>((resolve, reject) => {
          const req = run(d.transaction(storeName, mode).objectStore(storeName));
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
        }),
    );
  }

  return {
    async list() {
      const all = await tx<T[]>("readonly", (s) => s.getAll());
      return all.sort((a, b) => b.updatedAt - a.updatedAt);
    },
    listKeys: () =>
      tx<IDBValidKey[]>("readonly", (s) => s.getAllKeys()) as Promise<string[]>,
    get: (id) => tx<T | undefined>("readonly", (s) => s.get(id)),
    put: (item) => tx("readwrite", (s) => s.put(item)),
    remove: (id) => tx("readwrite", (s) => s.delete(id)),
  };
}

// Serialize a value to JSON and trigger a browser download.
export function downloadJson(value: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(value)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function newId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function safeFileName(name: string, fallback: string): string {
  return name.trim().replace(/[^\w.-]+/g, "_").slice(0, 60) || fallback;
}
